import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Languages } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { SITE, waLink } from "../data/site";
import { useLang } from "../i18n";

const NAV_KEYS = [
  ["navHome", "/"], ["navServices", "/services"], ["navFleet", "/fleet"], ["navOneway", "/one-way-taxi"],
  ["navAllraj", "/all-rajasthan-taxi"], ["navTours", "/tour-packages"], ["navGallery", "/gallery"],
  ["navAbout", "/about"], ["navBlog", "/blog"], ["navContact", "/contact"],
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { t, toggle } = useLang();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden md:block bg-navy text-white/85 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between">
          <p data-testid="topbar-address">Near Jhalamand, Navdurga Colony, Jodhpur, Rajasthan 342016</p>
          <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="topbar-phone">
            <Phone size={12} /> {SITE.phoneDisplay} · {t("topbarNote")}
          </a>
        </div>
      </div>
      <div
        className={`backdrop-blur-xl bg-white/95 border-b transition-shadow duration-300 ${
          scrolled ? "shadow-lg shadow-royal/5 border-royal/10" : "border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-1.5 sm:gap-4">
          <Link to="/" aria-label="Blue City Taxi home" data-testid="header-logo-link" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
            <img src="/logo.jpeg" alt="Blue City Taxi logo" className="h-10 sm:h-12 w-auto rounded-xl shadow-sm" />
            <span className="leading-none">
              <span className="block font-heading font-extrabold text-base sm:text-2xl tracking-tight text-navy whitespace-nowrap">
                BLUE CITY <span className="text-gold-dark">TAXI</span>
              </span>
              <span className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.24em] text-royal mt-1">
                Jodhpur · Rajasthan
              </span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5" aria-label="Main navigation">
            {NAV_KEYS.map(([key, to]) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${key.slice(3).toLowerCase()}`}
                className={({ isActive }) =>
                  `px-2 xl:px-2.5 py-2 rounded-lg text-[12.5px] font-semibold tracking-wide whitespace-nowrap transition-colors ${
                    isActive ? "text-royal bg-royal-50" : "text-slate-600 hover:text-royal hover:bg-royal-50/60"
                  }`
                }
              >
                {t(key)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={toggle}
              aria-label="Switch language"
              data-testid="lang-toggle-btn"
              className="inline-flex items-center gap-1 rounded-full border-2 border-gold/60 text-gold-dark text-[11px] sm:text-xs font-extrabold px-2.5 py-1.5 sm:py-2 hover:bg-gold hover:text-navy transition-colors"
            >
              <Languages size={13} /> {t("langBtn")}
            </button>
            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-royal text-white text-[13px] font-bold px-3.5 py-2.5 whitespace-nowrap hover:bg-royal-700 transition-colors"
            >
              <Phone size={14} /> {t("callNow")}
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-wa text-white text-[13px] font-bold px-3.5 py-2.5 whitespace-nowrap hover:brightness-95 transition"
            >
              <FaWhatsapp size={15} /> WhatsApp
            </a>
            <a href={`tel:${SITE.phoneTel}`} aria-label="Call now" data-testid="header-call-icon" className="sm:hidden p-2 rounded-full bg-royal text-white">
              <Phone size={15} />
            </a>
            <button
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 sm:p-2.5 rounded-full border border-slate-200 text-navy"
            >
              {open ? <X size={17} /> : <Menu size={17} />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl" aria-label="Mobile navigation">
            <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 gap-1.5">
              {NAV_KEYS.map(([key, to]) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`mobile-nav-${key.slice(3).toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-xl text-sm font-semibold ${
                      isActive ? "bg-royal text-white" : "bg-royal-50/70 text-royal-800"
                    }`
                  }
                >
                  {t(key)}
                </NavLink>
              ))}
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp"
                className="col-span-2 mt-1 px-3 py-2.5 rounded-xl text-sm font-bold bg-wa text-white text-center"
              >
                {t("whatsappBooking")}
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
